import React from "react";
import moment from "moment";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, find } from "lodash";
import { getMetaValue, getInitials } from "utils/typeUtils";
import type { ApprovalItem, User } from "types/models";

import ComparisonCard from "./ComparisonCard";
import AccountHolderInfoCard from "./AccountHolderInfo";

interface OwnProps {
  approval?: ApprovalItem;
  user?: User;
}

type Props = WithApolloClient<OwnProps>;

class YellowPathApproval extends React.PureComponent<Props> {
  render() {
    const firstName = get(this.props.user, "userProfile.firstName");
    const lastName = get(this.props.user, "userProfile.lastName");
    const email = get(this.props.approval, "correlationName", null);
    const dob = get(this.props.user, "userProfile.dob");
    const ssn = get(this.props.user, "fullSsn");
    const userId = get(this.props.approval, "entityIdentifier.id", "N/A");
    const phone = get(this.props.user, "userProfile.phoneNumber");
    const allReasons = get(this.props.approval, "allReasons");
    const submissionDate = get(this.props.approval, "createdAt");
    const silaWalletID = get(
      // user is optional but accessed unguarded in JS — preserve throw-on-undefined.
      // The shared `User` model (codegen) omits `accounts`; the original JS read
      // it as a loose array, so cast to a local accounts-bearing shape here
      // (mirrors NameChangeApproval) — runtime read unchanged.
      find(
        (this.props.user! as { accounts?: Record<string, unknown>[] }).accounts,
        { accountType: "WALLET" }
      ),
      "accountNumber",
      null
    );

    // getMetaValue's MetadataItem omits the nullable array members the schema
    // allows (it guards with i?.name at runtime) — bridge the shape here.
    const metadataItems = get(
      this.props.approval,
      "metadataItems",
      []
    ) as Parameters<typeof getMetaValue>[0];

    const accountId = getMetaValue(metadataItems, "accountId");
    const kycState = getMetaValue(metadataItems, "kycState");
    const apexAccount = getMetaValue(metadataItems, "Apex Account Number");

    const initials = getInitials(firstName, lastName);

    const formattedDate = submissionDate
      ? moment(submissionDate).format("dddd, MMMM Do YYYY")
      : null;

    return (
      <>
        <AccountHolderInfoCard
          initials={initials}
          firstName={firstName}
          lastName={lastName}
          email={email}
          dob={dob}
          ssn={ssn}
          userId={userId}
          submissionDate={formattedDate}
          apexAccount={apexAccount as React.ReactNode}
          accountId={accountId as React.ReactNode}
          kycState={kycState as React.ReactNode}
          kycStatus={kycState as React.ReactNode}
          silaWalletID={silaWalletID as React.ReactNode}
          phone={phone}
        />

        {/* getMetaValue / loose-record lookups above return `unknown` (account
            strings at runtime), and allReasons is codegen-nullable
            (`Maybe<string[]>`). The InfoItem/ComparisonCard children render them
            as React nodes / map over them unguarded just as the original JS did;
            the casts above/below preserve the exact runtime values. */}
        <ComparisonCard
          title="Yellow Path Review"
          changeType="yellowPath"
          changes={allReasons as string[]}
        />
      </>
    );
  }
}

export default withApollo<Props>(YellowPathApproval);
